import React, { useEffect, useState } from "react";
import { ShoppingBag, ArrowRight, X } from "lucide-react";
import { PACKAGES } from "../shared/catalog.js";
import { Brand, TreePine } from "./components.jsx";
import { Home } from "./home.jsx";
import { Admin } from "./workshop.jsx";
import { Customize, Cart, OrderPage } from "./cart.jsx";
import { Checkout, Confirmation } from "./checkout.jsx";
import { api } from "./api.js";
import { deadlineLabel as deadlineText } from "../shared/season.js";

function loadCart() {
  try {
    const value = JSON.parse(localStorage.getItem("whimsy-cart") || "[]");
    return Array.isArray(value)
      ? value.filter(
          (i) =>
            PACKAGES.some((p) => p.id === i.packageId) &&
            Array.isArray(i.children) &&
            i.children.length >= 1 &&
            i.children.length <= 20,
        )
      : [];
  } catch {
    return [];
  }
}

export function App() {
  const [path, setPath] = useState(location.pathname);
  const [config, setConfig] = useState(null),
    [configError, setConfigError] = useState("");
  const [cart, setCart] = useState(loadCart),
    [cartOpen, setCartOpen] = useState(false),
    [customizing, setCustomizing] = useState(null);
  const [confirmation, setConfirmation] = useState(() => {
      try {
        return JSON.parse(
          sessionStorage.getItem("whimsy-confirmation") || "null",
        );
      } catch {
        return null;
      }
    }),
    [mobileMenu, setMobileMenu] = useState(false);
  function navigate(to) {
    history.pushState({}, "", to);
    setPath(location.pathname);
    setMobileMenu(false);
    window.scrollTo(0, 0);
  }
  useEffect(() => {
    const handler = () => {
      setPath(location.pathname);
      window.scrollTo(0, 0);
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);
  useEffect(() => {
    api("/api/config")
      .then(setConfig)
      .catch(() =>
        setConfigError(
          "We couldn’t load ordering availability. Please refresh before ordering.",
        ),
      );
  }, [path]);
  useEffect(() => {
    localStorage.setItem("whimsy-cart", JSON.stringify(cart));
  }, [cart]);
  function updateCart(items) {
    setCart(items);
    sessionStorage.removeItem("whimsy-checkout-key");
  }
  function choose(packageId, index = null) {
    setCustomizing({
      packageId,
      index,
      item: index === null ? null : cart[index],
    });
  }
  const isAdmin = path.startsWith("/admin");
  if (isAdmin) return <Admin navigate={navigate} />;
  return (
    <>
      <div className="season-bar">
        <span>✦ A LITTLE NORTH POLE MAGIC, RIGHT HERE AT HOME ✦</span>
        <span>
          ORDERS CLOSE {deadlineText(config).toUpperCase()}{" "}
          <span className="bar-dot">•</span> DELIVERY DEC 6–12
        </span>
      </div>
      <header className="site-header">
        <Brand />
        <nav
          className={mobileMenu ? "nav-open" : ""}
          aria-label="Main navigation"
        >
          <a href="/#how-it-works">How it works</a>
          <a href="/#packages">Our packages</a>
          <a href="/#letters">Letters to Santa</a>
          <a href="/#about">Our story</a>
        </nav>
        <div className="header-actions">
          <button
            className="cart-toggle"
            onClick={() => setCartOpen(true)}
            aria-label={`Open cart, ${cart.length} packages`}
          >
            <ShoppingBag size={21} />
            <span className="cart-count">{cart.length}</span>
          </button>
          <a
            href="/order"
            className="button button-small"
            onClick={(e) => {
              e.preventDefault();
              navigate("/order");
            }}
          >
            Order a little magic <ArrowRight size={15} />
          </a>
          <button
            className="menu-toggle icon-button"
            aria-label="Toggle menu"
            aria-expanded={mobileMenu}
            onClick={() => setMobileMenu(!mobileMenu)}
          >
            {mobileMenu ? <X /> : <span>☰</span>}
          </button>
        </div>
      </header>
      {configError && (
        <div className="availability-error" role="alert">
          {configError}
        </div>
      )}
      {config && !config.acceptingOrders && (
        <div className="closed-banner">
          <TreePine size={19} />
          <span>
            We’re full for the season—thank you for making it magical!
          </span>
        </div>
      )}
      <main>
        {path === "/checkout" ? (
          <Checkout
            cart={cart}
            config={config}
            navigate={navigate}
            onEdit={(index) => choose(cart[index].packageId, index)}
            onSuccess={(order, checkoutUrl) => {
              setConfirmation(order);
              sessionStorage.setItem(
                "whimsy-confirmation",
                JSON.stringify(order),
              );
              updateCart([]);
              if (checkoutUrl) location.assign(checkoutUrl);
              else navigate("/thank-you");
            }}
          />
        ) : path === "/thank-you" ? (
          <Confirmation
            order={confirmation}
            config={config}
            navigate={navigate}
            onPaid={() => {
              const paid = { ...confirmation, received: confirmation.total };
              setConfirmation(paid);
              sessionStorage.setItem(
                "whimsy-confirmation",
                JSON.stringify(paid),
              );
            }}
          />
        ) : path === "/order" ? (
          <OrderPage
            config={config}
            cart={cart}
            choose={choose}
            navigate={navigate}
            openCart={() => setCartOpen(true)}
          />
        ) : (
          <Home config={config} onChoose={(id) => choose(id)} />
        )}
      </main>
      <footer className="site-footer">
        <div className="footer-top">
          <div>
            <Brand small />
            <p>
              Little surprises. Christmas memories.
              <br />
              Made with love, right here at home.
            </p>
          </div>
          <div>
            <span className="eyebrow">OUR LITTLE CORNER OF CHRISTMAS</span>
            <a href="/#packages">Our packages</a>
            <a href="/#letters">Letters & collection points</a>
            <a href="/#questions">Questions & answers</a>
          </div>
          <div>
            <span className="eyebrow">FROM OUR FAMILIES TO YOURS</span>
            <p>Idaho Falls · Ammon · Shelley · Rigby</p>
            <p>Maddy & Mackenzie</p>
            {config?.contactEmail &&
            !config.contactEmail.endsWith(".example") ? (
              <a
                className="test-contact"
                href={`mailto:${config.contactEmail}`}
              >
                {config.contactEmail}
              </a>
            ) : (
              <span className="test-contact">Contact email coming soon</span>
            )}
          </div>
        </div>
        <div className="footer-bottom">
          <span>
            © 2026 Whimsy Delivered · A little Christmas whimsy, delivered.
          </span>
          <button className="text-link" onClick={() => navigate("/admin")}>
            The workshop <span>↗</span>
          </button>
        </div>
      </footer>
      {customizing && (
        <Customize
          key={`${customizing.packageId}-${customizing.index}`}
          value={customizing}
          onClose={() => setCustomizing(null)}
          disabled={!config?.acceptingOrders}
          onAdd={(item) => {
            updateCart(
              customizing.index === null
                ? [...cart, item]
                : cart.map((v, i) => (i === customizing.index ? item : v)),
            );
            setCustomizing(null);
            if (path !== "/checkout") setCartOpen(true);
          }}
        />
      )}
      {cartOpen && (
        <Cart
          cart={cart}
          config={config}
          onClose={() => setCartOpen(false)}
          onEdit={(i) => {
            setCartOpen(false);
            choose(cart[i].packageId, i);
          }}
          onRemove={(index) => updateCart(cart.filter((_, i) => i !== index))}
          onCheckout={() => {
            setCartOpen(false);
            navigate("/checkout");
          }}
          onShop={() => {
            setCartOpen(false);
            navigate("/order");
          }}
        />
      )}
    </>
  );
}
